<template>
  <v-card outlined class="gas-cylinder-guide">
    <v-card-title class="py-2 text-subtitle-1 font-weight-bold d-flex justify-space-between align-center flex-wrap">
      <div class="d-flex align-center">
        <v-icon left color="primary">mdi-gas-cylinder</v-icon>
        <span>Kaasupullojen kaulojen värikoodit (SFS-EN 1089-3)</span>
      </div>
      <v-chip color="secondary" outlined class="my-1 font-weight-bold">
        Standardi SFS-EN 1089-3
      </v-chip>
    </v-card-title>

    <v-card-subtitle class="text-body-2 pt-1 pb-3">
      Kaasupullon hartiaosan (kaulan) väri kertoo standardin SFS-EN 1089-3 mukaisesti kaasun vaaraominaisuudet tai spesifisen kaasun.
      <br />
      <span class="grey--text text--darken-2">
        <strong>Tärkeää:</strong> Pullon virallinen varoitusetiketti ja leimaus ovat aina ensisijainen tunnistuskeino.
      </span>
    </v-card-subtitle>

    <v-divider />

    <v-card-text class="pa-3">
      <v-row>
        <!-- Left: Search and Color Selector -->
        <v-col cols="12" md="4" lg="4">
          <!-- Text Search Filter -->
          <v-text-field
            v-model="searchFilter"
            label="Etsi kaasua tai väriä"
            placeholder="esim. asetyleeni, happi, CO2, ilma..."
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
            hide-details="auto"
            class="mb-3"
          />

          <!-- Color Quick Selector Chips -->
          <div class="mb-3">
            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-2">
              Valitse hartian (kaulan) väri:
            </div>
            <div class="d-flex flex-wrap color-palette">
              <v-btn
                v-for="color in availableColors"
                :key="color.name"
                small
                depressed
                class="mr-1 mb-1 color-pill font-weight-bold"
                :style="{
                  backgroundColor: color.hex,
                  color: color.darkText ? '#111111' : '#ffffff',
                  border: color.border ? '1px solid #999' : 'none',
                }"
                @click="filterByColor(color.name)"
              >
                {{ color.name }}
              </v-btn>
              <v-btn
                small
                outlined
                class="mr-1 mb-1 font-weight-bold"
                @click="searchFilter = ''"
              >
                Kaikki
              </v-btn>
            </div>
          </div>

          <!-- Gas List -->
          <v-list dense class="elevation-1 rounded gas-list" style="max-height: 480px; overflow-y: auto">
            <v-list-item-group v-model="selectedGasIndex" color="primary" mandatory>
              <v-list-item
                v-for="(gas, idx) in filteredGases"
                :key="idx"
                class="py-1"
                @click="selectedGas = gas"
              >
                <!-- Shoulder Color Dot / Indicator -->
                <v-list-item-avatar size="28" class="my-0 mr-3">
                  <div
                    class="shoulder-dot"
                    :style="getDotStyle(gas)"
                  />
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="text-body-1 font-weight-bold">
                    {{ gas.name }}
                    <span v-if="gas.cylinderLabel" class="text-body-2 primary--text ml-1">
                      [{{ gas.cylinderLabel }}]
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    Kaula: <strong>{{ gas.shoulderColorName }}</strong>
                    <br />
                    <span class="grey--text text--darken-1">{{ gas.hazardCategory }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-col>

        <!-- Right: Visual Cylinder & Tactical Hazard Analysis -->
        <v-col cols="12" md="8" lg="8">
          <div v-if="selectedGas">
            <v-card outlined class="pa-4">
              <v-row dense>
                <!-- SVG Cylinder Graphic -->
                <v-col cols="12" sm="5" md="4" class="d-flex flex-column align-center justify-center">
                  <div class="cylinder-svg-wrapper mb-2">
                    <svg viewBox="0 0 140 320" class="cylinder-svg" xmlns="http://www.w3.org/2000/svg">
                      <!-- Cap / Valve at Top -->
                      <rect x="58" y="10" width="24" height="22" rx="3" fill="#424242" stroke="#212121" stroke-width="2" />
                      <rect x="52" y="26" width="36" height="12" rx="2" fill="#757575" stroke="#424242" stroke-width="1.5" />
                      <!-- Valve outlet -->
                      <rect x="84" y="16" width="12" height="10" rx="1" fill="#9e9e9e" stroke="#424242" stroke-width="1.5" />

                      <!-- Collar Ring -->
                      <path d="M 40 40 L 100 40 L 105 48 L 35 48 Z" fill="#616161" stroke="#212121" stroke-width="2" />

                      <!-- CYLINDER SHOULDER (Kaula / Hartia) -->
                      <g v-if="selectedGas.splitShoulder">
                        <!-- Dual-colored shoulder (e.g. Breathing Air: White & Black quarters) -->
                        <path d="M 35 48 Q 20 80 20 100 L 70 100 L 70 48 Z" :fill="selectedGas.splitColors[0]" stroke="#212121" stroke-width="2" />
                        <path d="M 70 48 L 70 100 L 120 100 Q 120 80 105 48 Z" :fill="selectedGas.splitColors[1]" stroke="#212121" stroke-width="2" />
                      </g>
                      <g v-else>
                        <!-- Single-colored shoulder -->
                        <path
                          d="M 35 48 Q 20 80 20 100 L 120 100 Q 120 80 105 48 Z"
                          :fill="selectedGas.shoulderHex"
                          stroke="#212121"
                          stroke-width="2"
                        />
                      </g>

                      <!-- CYLINDER BODY (Lieriö / Runko) -->
                      <rect
                        x="20"
                        y="100"
                        width="100"
                        height="180"
                        rx="0"
                        :fill="selectedGas.bodyHex"
                        stroke="#212121"
                        stroke-width="2"
                      />
                      <!-- Base curve -->
                      <path d="M 20 280 Q 20 305 70 305 Q 120 305 120 280 Z" :fill="selectedGas.bodyHex" stroke="#212121" stroke-width="2" />

                      <!-- Spec / Safety Label Placeholder on body -->
                      <rect x="36" y="125" width="68" height="48" rx="3" fill="#ffffff" stroke="#9e9e9e" stroke-width="1" />
                      <line x1="42" y1="136" x2="98" y2="136" stroke="#424242" stroke-width="2" />
                      <line x1="42" y1="145" x2="88" y2="145" stroke="#757575" stroke-width="1.5" />
                      <line x1="42" y1="154" x2="94" y2="154" stroke="#757575" stroke-width="1.5" />
                      <rect x="74" y="148" width="16" height="16" fill="#f44336" rx="1" />

                      <!-- Cylinder Body Text Badge: Fits completely inside cylinder width (20 to 120) -->
                      <text
                        x="70"
                        y="232"
                        text-anchor="middle"
                        :font-size="getCylinderFontSize(selectedGas)"
                        font-weight="900"
                        :fill="selectedGas.bodyTextColor || '#ffffff'"
                        letter-spacing="1.5"
                        style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;"
                      >
                        {{ getCylinderLabel(selectedGas) }}
                      </text>
                    </svg>
                  </div>

                  <!-- Cylinder Color Metadata Card below SVG -->
                  <v-sheet
                    outlined
                    rounded
                    class="pa-3 mt-1 text-center cylinder-info-card"
                    width="100%"
                  >
                    <div class="text-subtitle-2 font-weight-bold mb-1">
                      Hartian väri:
                      <div class="primary--text font-weight-black text-body-1">
                        {{ selectedGas.shoulderColorName }}
                      </div>
                    </div>
                    <div class="text-body-2 mb-1">
                      Runko: <strong>{{ selectedGas.bodyColorName }}</strong>
                    </div>
                    <div v-if="selectedGas.formula" class="text-body-2 font-weight-medium grey--text text--darken-2 mt-1">
                      {{ selectedGas.formula }}
                    </div>
                  </v-sheet>
                </v-col>

                <!-- Tactical Hazard Analysis -->
                <v-col cols="12" sm="7" md="8" class="pl-sm-4">
                  <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                    <div>
                      <h2 class="text-h5 font-weight-black primary--text mb-1">
                        {{ selectedGas.name }}
                      </h2>
                      <div v-if="selectedGas.formula" class="text-subtitle-1 font-weight-medium grey--text text--darken-2 mb-1">
                        Kaava / seos: <strong>{{ selectedGas.formula }}</strong>
                      </div>
                      <div class="text-body-1 font-weight-medium mb-2" style="line-height: 1.5;">
                        {{ selectedGas.description }}
                      </div>
                    </div>
                    <v-chip
                      :color="getHazardBadgeColor(selectedGas)"
                      dark
                      class="font-weight-bold my-1 text-body-2"
                    >
                      {{ selectedGas.hazardCategory }}
                    </v-chip>
                  </div>

                  <!-- Critical Alert for High-Risk Gases (Acetylene, Oxygen, Chlorine, Hydrogen, LPG) -->
                  <v-alert
                    v-if="selectedGas.criticalAlert"
                    type="error"
                    dense
                    outlined
                    prominent
                    class="mb-3 text-body-1 font-weight-medium"
                    icon="mdi-alert"
                  >
                    {{ selectedGas.criticalAlert }}
                  </v-alert>

                  <!-- Technical & Hazard Breakdown Cards -->
                  <v-row dense class="mb-2">
                    <!-- Flammability / Explosion -->
                    <v-col cols="12" sm="6">
                      <v-sheet outlined rounded class="pa-3 fill-height border-fire">
                        <div class="d-flex align-center text-subtitle-2 font-weight-bold red--text text--darken-3 mb-1">
                          <v-icon small left color="error">mdi-fire</v-icon>
                          Palo- ja räjähdysvaara
                        </div>
                        <div class="text-body-2" style="line-height: 1.55;">
                          {{ selectedGas.flammabilityInfo }}
                        </div>
                      </v-sheet>
                    </v-col>

                    <!-- Toxicity & Health -->
                    <v-col cols="12" sm="6">
                      <v-sheet outlined rounded class="pa-3 fill-height border-toxic">
                        <div class="d-flex align-center text-subtitle-2 font-weight-bold amber--text text--darken-4 mb-1">
                          <v-icon small left color="amber darken-4">mdi-skull-crossbones</v-icon>
                          Terveysvaara & Myrkyllisyys
                        </div>
                        <div class="text-body-2" style="line-height: 1.55;">
                          {{ selectedGas.toxicityInfo }}
                        </div>
                      </v-sheet>
                    </v-col>

                    <!-- Density & Behavior in Air -->
                    <v-col cols="12" sm="6">
                      <v-sheet outlined rounded class="pa-3 fill-height border-density">
                        <div class="d-flex align-center text-subtitle-2 font-weight-bold blue--text text--darken-3 mb-1">
                          <v-icon small left color="blue darken-3">mdi-arrow-up-down</v-icon>
                          Käyttäytyminen & Tiheys
                        </div>
                        <div class="text-body-2" style="line-height: 1.55;">
                          {{ selectedGas.densityInfo }}
                        </div>
                      </v-sheet>
                    </v-col>

                    <!-- Typical Pressure & Storage -->
                    <v-col cols="12" sm="6">
                      <v-sheet outlined rounded class="pa-3 fill-height border-pressure">
                        <div class="d-flex align-center text-subtitle-2 font-weight-bold purple--text text--darken-3 mb-1">
                          <v-icon small left color="purple darken-3">mdi-gauge</v-icon>
                          Paine & Säilytys
                        </div>
                        <div class="text-body-2" style="line-height: 1.55;">
                          {{ selectedGas.pressureInfo }}
                        </div>
                      </v-sheet>
                    </v-col>
                  </v-row>

                  <!-- Fire Service Action Instructions -->
                  <v-card outlined class="pa-3 mt-2 tactical-actions-card grey lighten-5">
                    <div class="font-weight-bold text-subtitle-1 mb-2 d-flex align-center">
                      <v-icon small left color="primary">mdi-shield-fire</v-icon>
                      Toimintaohjeet tulipalossa ja onnettomuustilanteessa:
                    </div>
                    <ul class="pl-4 text-body-1 mb-2">
                      <li
                        v-for="(action, aIdx) in selectedGas.tacticalActions"
                        :key="aIdx"
                        class="mb-2"
                        style="line-height: 1.5;"
                      >
                        {{ action }}
                      </li>
                    </ul>

                    <div v-if="selectedGas.tokevaInstruction" class="mt-3 text-right">
                      <v-btn
                        small
                        color="error"
                        outlined
                        class="font-weight-bold"
                        @click="$emit('open-instruction', selectedGas.tokevaInstruction)"
                      >
                        <v-icon left small>mdi-book-open-outline</v-icon>
                        Avaa TOKEVA {{ selectedGas.tokevaInstruction }}
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </div>
          <v-card v-else outlined class="pa-6 text-center">
            <v-icon size="64" color="grey lighten-1">mdi-gas-cylinder</v-icon>
            <div class="text-h6 font-weight-medium grey--text text--darken-1 mt-2">
              Valitse kaasu listalta tai kaulan väri vasemmalta
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "GasCylinderGuide",
  emits: ["open-instruction"],
  data: () => ({
    searchFilter: "",
    selectedGasIndex: 0,
    selectedGas: null,

    availableColors: [
      { name: "Kastanjanruskea", hex: "#6d2a2a" },
      { name: "Punainen", hex: "#d32f2f" },
      { name: "Keltainen", hex: "#fbc02d", darkText: true },
      { name: "Vaaleansininen", hex: "#4fc3f7", darkText: true },
      { name: "Valkoinen", hex: "#ffffff", darkText: true, border: true },
      { name: "Musta", hex: "#212121" },
      { name: "Harmaa", hex: "#757575" },
      { name: "Vihreä", hex: "#388e3c" },
      { name: "Ruskea", hex: "#795548" },
      { name: "Musta-valkoinen", hex: "#9e9e9e", darkText: true },
    ],

    gases: [
      {
        name: "Asetyleeni",
        formula: "C₂H₂",
        shortName: "C2H2",
        cylinderLabel: "C₂H₂",
        shoulderColorName: "Kastanjanruskea / Puna-ruskea (RAL 3009)",
        shoulderHex: "#6d2a2a",
        bodyColorName: "Kastanjanruskea tai Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Erittäin helposti syttyvä & epävakaa",
        description: "Polttoleikkauksessa ja hitsauksessa käytettävä teollisuuskaasu.",
        criticalAlert: "PELASTUSTOIMEN VAARALLISIN KAASU! Asetyleeni voi käynnistää sisäisen hajoamisreaktion ilman happeakin. Lämpeneminen johtaa erittäin tuhoisaan räjähdykseen. Suojaetäisyys vähintään 300 m. Vaatii 24 h jatkuvan jäähdytyksen tai hallitun ammunnan.",
        flammabilityInfo: "Erittäin laaja syttymisalue (2,4 – 83 %). Syttyy herkästi pienimmästäkin kipinästä. Paineenalainen hajoamisreaktio.",
        toxicityInfo: "Ei varsinaisesti myrkyllinen, mutta tukehtumiskaasu suurina pitoisuuksina. Teollinen kaasu sisältää usein valkosipulimaista hajua (fosfiini/rikki).",
        densityInfo: "Hieman ilmaa kevyempi (tiheys 0,91 x ilma). Nousee ylöspäin ja sekoittuu herkästi huoneilmaan.",
        pressureInfo: "Liuotettuna asetoniin huokoiseen massaan, paine n. 15–18 bar. Pulloa ei saa koskaan käyttää tai säilyttää vaaka-asennossa!",
        tacticalActions: [
          "Suojaetäisyys vähintään 300 metriä kaikista suunnista.",
          "Tarkista pullon lämpötila lämpökameralla turvallisen etäisyyden ja suojan takaa.",
          "Jos pullo on kuumentunut tai ollut tulipalossa: Jäähdytä runsaalla vedellä miehittämättömällä monitorilla vähintään 24 tuntia.",
          "Älä koske tai siirrä kuumentunutta pulloa missään tapauksessa.",
          "Poliisin TEPO-yksikkö voi tarvittaessa ampua pullon hallitusti paineen purkamiseksi.",
        ],
        tokevaInstruction: "T2d",
      },
      {
        name: "Happi (Teollisuus- ja lääkehappi)",
        formula: "O₂",
        shortName: "O2",
        cylinderLabel: "O₂",
        shoulderColorName: "Valkoinen (RAL 9010)",
        shoulderHex: "#ffffff",
        bodyColorName: "Harmaa (teollisuus) tai Valkoinen (lääkehappi)",
        bodyHex: "#616161",
        bodyTextColor: "#ffffff",
        hazardCategory: "Voimakkaasti hapettava",
        description: "Elintoimintoja ja palamista ylläpitävä kaasu.",
        criticalAlert: "KIELTO: Ei koskaan öljyä, rasvaa tai rasvaisia käsineitä venttiileihin! Paineistettu happi reagoi orgaanisen aineen kanssa räjähdysmäisesti ilman sytytyslähdettä. Vaatteisiin imeytynyt happi tekee niistä räjähdysherkästi syttyviä.",
        flammabilityInfo: "Ei pala itse, mutta kiihdyttää paloa poikkeuksellisen rajusti. Nostaa palamislämpötiloja ja tekee syttymättömistäkin materiaaleista palavia.",
        toxicityInfo: "Myrkytön normaali-ilmanpaineessa.",
        densityInfo: "Hieman ilmaa raskaampi (tiheys 1,1 x ilma). Voi kertyä lattioille ja syvennyksiin suljetuissa tiloissa.",
        pressureInfo: "Korkeapainekaasu, tavallisesti 200 bar (tai 300 bar).",
        tacticalActions: [
          "Tuuleta huolellisesti tila, jos happea on vuotanut.",
          "Jos sammuttajan vaatteisiin on tarttunut happea, pysy kaukana kipinöistä ja avotulesta vähintään 15 minuuttia.",
          "Sulje pullon pääventtiili, jos se on turvallisesti tehtävissä.",
          "Jäähdytä pulloa suojasta, jos se on tulipalon lämmittämä.",
        ],
        tokevaInstruction: "T2a",
      },
      {
        name: "Hengitysilma (Savusukellus- ja sukelluspullot)",
        formula: "Ilma (21% O₂ + 78% N₂)",
        shortName: "Air",
        cylinderLabel: "ILMA",
        shoulderColorName: "Musta-valkoinen (neljännekset)",
        splitShoulder: true,
        splitColors: ["#ffffff", "#212121"],
        bodyColorName: "Keltainen (savusukellus) tai Harmaa",
        bodyHex: "#fbc02d",
        bodyTextColor: "#111111",
        hazardCategory: "Paineistettu hengityskaasu (200 / 300 bar)",
        description: "Paineilmalaitteissa (PA-laite) käytettävä puhdas hengitysilma.",
        criticalAlert: "Paineastiavaara: Komposiitti- tai teräspullon paine on 200–300 bar. Kuumuudessa pullon sisäinen paine nousee rajusti -> murtuessaan hengenvaarallinen sirpale- ja rekyylivaara.",
        flammabilityInfo: "Normaali-ilman happipitoisuus (21 %). Ylläpitää palamista.",
        toxicityInfo: "Myrkytön puhdas hengitysilma.",
        densityInfo: "Ilman tiheys (1,0).",
        pressureInfo: "Erittäin korkea käyttöpaine: 200 bar tai 300 bar.",
        tacticalActions: [
          "Suojaa säiliöt kuumuudelta ja mekaanisilta iskuilta.",
          "Tulipalotilanteessa jäähdytä vesisuihkulla ja siirrä turvaan heti kun mahdollista.",
          "Jos venttiili murtuu, pullo voi lentää ohjuksen tavoin satoja metrejä.",
        ],
        tokevaInstruction: "T2a",
      },
      {
        name: "Vety",
        formula: "H₂",
        shortName: "H2",
        cylinderLabel: "H₂",
        shoulderColorName: "Punainen (RAL 3000)",
        shoulderHex: "#d32f2f",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Erittäin helposti syttyvä kaasu",
        description: "Teollisuus-, polttokenno- ja laboratorio-kaasu.",
        criticalAlert: "HUOM: Vetypalo palaa päivänvalossa täysin NÄKYMÄTTÖMÄLLÄ liekillä! Käytä aina lämpökameraa tai pitkävartista harjaa liekin havaitsemiseen. Laaja syttymisalue (4 – 75 %).",
        flammabilityInfo: "Syttyy äärimmäisen herkästi pienimmästäkin staattisesta sähköstä. Syttymisenergia on vain 0,02 mJ (huomaamaton kipinä riittää).",
        toxicityInfo: "Myrkytön, mutta syrjäyttää hapen katonrajassa.",
        densityInfo: "Erittäin paljon ilmaa kevyempi (tiheys 0,07 x ilma!). Kohoaa välittömästi kattoon ja tuuletusaukkoihin.",
        pressureInfo: "Korkeapaine 200 bar, ajoneuvoissa jopa 700 bar.",
        tacticalActions: [
          "Käytä lämpökameraa liekin toteamiseen.",
          "Älä sammuta palavaa vetyliekiä, ellei vuotoa voida sulkea välittömästi (sammuttaminen ilman sulkua aiheuttaa massiivisen räjähdysvaaran!).",
          "Tuuleta yläpohjat ja kattorakenteet.",
        ],
        tokevaInstruction: "T2c",
      },
      {
        name: "Hiilidioksidi",
        formula: "CO₂",
        shortName: "CO2",
        cylinderLabel: "CO₂",
        shoulderColorName: "Harmaa (RAL 7037)",
        shoulderHex: "#757575",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Inertti tukehtumiskaasu (Ilmaa raskaampi)",
        description: "Sammutuskaasu, panimokaasu ja teollisuuden suojakaasu.",
        criticalAlert: "VAARA: 1,5 kertaa ilmaa raskaampaa! Valuu huomaamattomasti kellareihin, kaivoihin, viemäreihin ja syvennyksiin. Syrjäyttää hapen ja aiheuttaa välittömän tajuttomuuden ilman varoitusoireita.",
        flammabilityInfo: "Palamaton, toimii sammutteena.",
        toxicityInfo: "Tukehtumiskaasu. Pitoisuus yli 5 % aiheuttaa sekavuutta ja yli 10 % nopean tajuttomuuden.",
        densityInfo: "Ilmaa selvästi raskaampi (tiheys 1,5 x ilma).",
        pressureInfo: "Nesteytetty paineenalainen kaasu, paine n. 50–60 bar huoneenlämmössä.",
        tacticalActions: [
          "Mittaa happipitoisuus ja CO₂ ennen kellaritiloihin menoa.",
          "Paineenalaisena purkautuva neste muodostaa kuivajäätä (-78,5 °C) -> paha paleltumavammariski.",
          "Käytä paineilmahengityslaitetta aina suljetuissa tiloissa.",
        ],
        tokevaInstruction: "T2b",
      },
      {
        name: "Typpi",
        formula: "N₂",
        shortName: "N2",
        cylinderLabel: "N₂",
        shoulderColorName: "Musta (RAL 9005)",
        shoulderHex: "#212121",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Inertti tukehtumiskaasu",
        description: "Yleisin teollisuuden suojakaasu, renkaantäyttö ja laboratoriot.",
        criticalAlert: "Hajuton, väritön ja mauton. Hengitettynä puhtaassa typessä ihminen menettää tajuntansa 1-2 hengenvedolla ilman tukehtumisen tunnetta (hapenpuuteverikoe ei laukea).",
        flammabilityInfo: "Palamaton.",
        toxicityInfo: "Fysiologisesti inertti, vaara perustuu pelkkään hapen syrjäyttämiseen.",
        densityInfo: "Lähes sama kuin ilman (tiheys 0,97 x ilma). Sekoittuu ilmaan.",
        pressureInfo: "Korkeapainekaasu (200 bar) tai nestemäisenä typpenä kryosäiliössä (-196 °C).",
        tacticalActions: [
          "Käytä paineilmalaitetta suljetussa tilassa.",
          "Tuuleta tila huolellisesti ennen suojaimista luopumista.",
        ],
        tokevaInstruction: "T2b",
      },
      {
        name: "Argon",
        formula: "Ar",
        shortName: "Ar",
        cylinderLabel: "Ar",
        shoulderColorName: "Tummanvihreä (RAL 6001)",
        shoulderHex: "#2e7d32",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Inertti tukehtumiskaasu",
        description: "MIG/TIG-hitsauksessa käytettävä suojakaasu.",
        criticalAlert: "Ilmaa raskaampi (tiheys 1,38 x ilma). Kerääntyy hitsausmonttuihin, säiliöiden pohjalle ja kellareihin.",
        flammabilityInfo: "Palamaton jalokaasu.",
        toxicityInfo: "Tukehtumisvaara.",
        densityInfo: "Ilmaa raskaampi.",
        pressureInfo: "Korkeapainekaasu (200 bar tai 300 bar).",
        tacticalActions: [
          "Tuuleta alavat tilat ja mittaa happipitoisuus.",
        ],
        tokevaInstruction: "T2b",
      },
      {
        name: "Kloori",
        formula: "Cl₂",
        shortName: "Cl2",
        cylinderLabel: "Cl₂",
        shoulderColorName: "Keltainen (RAL 1018)",
        shoulderHex: "#fbc02d",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Myrkyllinen, syövyttävä & hapettava",
        description: "Vedenpuhdistamoilla, uimahalleissa ja teollisuudessa käytettävä myrkkykaasu.",
        criticalAlert: "HENGENVAARA: Pistävä, kellanvihreä erittäin myrkyllinen kaasu. Aiheuttaa hengitysteiden syöpymistä ja hengenvaarallisen keuhkopöhön viiveellä (2-24 h). Vaatii kaasutiiviin kemikaalisuojapuvun!",
        flammabilityInfo: "Palamaton, mutta voimakkaasti hapettava (tukee ja kiihdyttää paloa).",
        toxicityInfo: "Erittäin myrkyllistä (LC50 pieni). Välitön syövyttävä vaikutus kosteille limakalvoille.",
        densityInfo: "2,5 kertaa ilmaa raskaampaa! Valuu maata pitkin ja muodostaa tiheitä laaksoihin kertyviä kaasupilviä.",
        pressureInfo: "Paineenalaisena nesteytetty kaasu (n. 7 bar).",
        tacticalActions: [
          "Kaasutiivis kemikaalisuojapuku ja paineilmalaite pakolliset.",
          "Vesiverho kaasupilven sitomiseen (älä suihkuta vettä suoraan nestevuotoon!).",
          "Evakuoi ja eristä laaja vaara-alue tuulen alapuolelta.",
        ],
        tokevaInstruction: "T2h",
      },
      {
        name: "Helium",
        formula: "He",
        shortName: "He",
        cylinderLabel: "He",
        shoulderColorName: "Oliivinruskea / Ruskea (RAL 8008)",
        shoulderHex: "#795548",
        bodyColorName: "Harmaa (RAL 7037)",
        bodyHex: "#424242",
        bodyTextColor: "#ffffff",
        hazardCategory: "Inertti tukehtumiskaasu (Kevyt)",
        description: "Laboratorio-, mittalaite-, pallo- ja sukelluskaasu.",
        criticalAlert: "Erittäin kevyt kaasu. Syrjäyttää hapen katonrajassa ja ahtaissa suljetuissa tiloissa.",
        flammabilityInfo: "Palamaton jalokaasu.",
        toxicityInfo: "Tukehtumisvaara.",
        densityInfo: "Hyvin paljon ilmaa kevyempi (tiheys 0,14 x ilma).",
        pressureInfo: "200 bar korkeapainekaasu.",
        tacticalActions: [
          "Tuuleta yläpohjat ja katonrajat.",
        ],
        tokevaInstruction: "T2b",
      },
      {
        name: "Lääkinnällinen ilokaasu (Typpioksiduuli)",
        formula: "N₂O",
        shortName: "N2O",
        cylinderLabel: "N₂O",
        shoulderColorName: "Tummansininen (RAL 5010)",
        shoulderHex: "#1565c0",
        bodyColorName: "Valkoinen (lääkinnällinen)",
        bodyHex: "#eeeeee",
        bodyTextColor: "#111111",
        hazardCategory: "Hapettava & Huumaava kaasu",
        description: "Kivunlievityksessä ja anestesiassa käytettävä lääkekaasu.",
        criticalAlert: "Voimakkaasti paloa kiihdyttävä (vapauttaa happea kuumentuessa). Sisäänhengitettynä huumaava vaikutus.",
        flammabilityInfo: "Hapettava, edistää palamista rajusti.",
        toxicityInfo: "Huumaava ja hapenpuutetta aiheuttava.",
        densityInfo: "Ilmaa raskaampi (tiheys 1,5 x ilma).",
        pressureInfo: "Paineenalaisena nesteytetty kaasu (n. 50 bar).",
        tacticalActions: [
          "Ei rasvoja eikä syttymislähteitä lähelle.",
          "Jäähdytä pulloa tulipalossa.",
        ],
        tokevaInstruction: "T2a",
      },
      {
        name: "Nestekaasu (Propaani / Butaani)",
        formula: "C₃H₈ / C₄H₁₀",
        shortName: "LPG",
        cylinderLabel: "LPG",
        shoulderColorName: "Ei erillistä hartiaväriä (teräspullo: sininen/harmaa, komposiitti: oranssi)",
        shoulderHex: "#1e88e5",
        bodyColorName: "Sininen, Harmaa tai Oranssi",
        bodyHex: "#1565c0",
        bodyTextColor: "#ffffff",
        hazardCategory: "Erittäin helposti syttyvä (Ilmaa raskaampi)",
        description: "Yleisin kotitalous- ja retkikaasu, polttoaineena ja lämmityksessä.",
        criticalAlert: "BLEVE-VAARA! Tulipalossa nestekaasupullo kuumenee -> nestekaasu kiehuu -> säiliö repeää katastrofaalisesti (tulipallo ja sirpaleet jopa satoja metrejä). Ilmaa raskaampaa: valuu kellareihin ja kaivoihin.",
        flammabilityInfo: "Erittäin herkästi syttyvä (syttymisalue n. 2–10 %).",
        toxicityInfo: "Hajustettu voimakkaasti turvallisuussyistä. Suurina pitoisuuksina huumaava ja tukehtumiskaasu.",
        densityInfo: "Ilmaa selvästi raskaampaa (tiheys 1,5–2 x ilma).",
        pressureInfo: "Nesteytetty kaasuna paineenalaisena (n. 5–10 bar).",
        tacticalActions: [
          "Suojaetäisyys vähintään 150 metriä.",
          "Jäähdytä pulloa runsaalla vedellä turvasta heti.",
          "Jos kaasua palaa venttiilistä: Älä sammuta liekkiä ennen kuin pullo on saatu suljettua!",
          "Tarkista aina kellarit, lattiakaivot ja montut.",
        ],
        tokevaInstruction: "T2g",
      },
    ],
  }),
  computed: {
    filteredGases() {
      const q = (this.searchFilter || "").trim().toLowerCase();
      if (!q) return this.gases;
      return this.gases.filter((g) => {
        return (
          g.name.toLowerCase().includes(q) ||
          (g.formula && g.formula.toLowerCase().includes(q)) ||
          (g.cylinderLabel && g.cylinderLabel.toLowerCase().includes(q)) ||
          g.shoulderColorName.toLowerCase().includes(q) ||
          g.hazardCategory.toLowerCase().includes(q) ||
          g.description.toLowerCase().includes(q)
        );
      });
    },
  },
  watch: {
    filteredGases(newVal) {
      if (newVal.length > 0 && !newVal.includes(this.selectedGas)) {
        this.selectedGas = newVal[0];
        this.selectedGasIndex = 0;
      }
    },
  },
  created() {
    this.selectedGas = this.gases[0];
  },
  methods: {
    filterByColor(colorName) {
      this.searchFilter = colorName;
    },
    getCylinderLabel(gas) {
      if (!gas) return "";
      if (gas.cylinderLabel) return gas.cylinderLabel;
      if (gas.shortName && gas.shortName.length <= 6) return gas.shortName;
      if (gas.formula && gas.formula.length <= 6) return gas.formula;
      return gas.name.slice(0, 5);
    },
    getCylinderFontSize(gas) {
      const label = this.getCylinderLabel(gas);
      if (label.length > 5) return 15;
      if (label.length > 3) return 18;
      return 20;
    },
    getDotStyle(gas) {
      if (gas.splitShoulder) {
        return {
          background: `linear-gradient(135deg, ${gas.splitColors[0]} 50%, ${gas.splitColors[1]} 50%)`,
          border: "1px solid #777",
        };
      }
      return {
        backgroundColor: gas.shoulderHex,
        border: gas.shoulderHex === "#ffffff" ? "1px solid #999" : "none",
      };
    },
    getHazardBadgeColor(gas) {
      const cat = (gas.hazardCategory || "").toLowerCase();
      if (cat.includes("epävakaa") || cat.includes("syttyvä")) return "error";
      if (cat.includes("myrkyllinen")) return "amber darken-4";
      if (cat.includes("hapettava")) return "light-blue darken-2";
      if (cat.includes("inertti")) return "green darken-2";
      return "grey darken-2";
    },
  },
};
</script>

<style scoped lang="scss">
.gas-cylinder-guide {
  .shoulder-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .color-pill {
    text-transform: none;
    font-size: 0.85rem;
    height: 28px !important;
    padding: 0 10px !important;
  }

  .cylinder-svg-wrapper {
    width: 140px;
    height: 320px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  }

  .cylinder-svg {
    width: 100%;
    height: 100%;
  }

  .border-fire {
    border-left: 4px solid #d32f2f !important;
    background-color: #ffebee;
  }
  .border-toxic {
    border-left: 4px solid #ff8f00 !important;
    background-color: #fff8e1;
  }
  .border-density {
    border-left: 4px solid #1976d2 !important;
    background-color: #e3f2fd;
  }
  .border-pressure {
    border-left: 4px solid #7b1fa2 !important;
    background-color: #f3e5f5;
  }
}

/* Dark mode enhancements */
.theme--dark {
  .gas-cylinder-guide {
    .border-fire {
      background-color: #2c1515 !important;
      border-color: #5c2222 !important;
      color: #ffcdd2;
    }
    .border-toxic {
      background-color: #2b2210 !important;
      border-color: #5d4013 !important;
      color: #ffe0b2;
    }
    .border-density {
      background-color: #102438 !important;
      border-color: #173b5c !important;
      color: #bbdefb;
    }
    .border-pressure {
      background-color: #24142e !important;
      border-color: #49235e !important;
      color: #e1bee7;
    }
    .tactical-actions-card {
      background-color: #1e2227 !important;
      border-color: #363c45 !important;
    }
    .cylinder-info-card {
      background-color: #1e2227 !important;
      border-color: #363c45 !important;
    }
  }
}
</style>
